export const BREAKFAST_PRICE = 5;

export interface ChildRange {
  min: number;
  max: number;
  price: number;
}

export interface QuoteInput {
  room: any;
  adults: number;
  children: number;
  childAges: number[];
  nights: number;
  breakfast: boolean;
}

export interface Quote {
  nights: number;
  roomPerNight: number;
  roomTotal: number;
  includedPersons: number;
  extraAdults: { count: number; perNight: number; total: number };
  children: { index: number; age: number; perNight: number }[];
  childrenPerNight: number;
  childrenTotal: number;
  breakfastCount: number;
  breakfastTotal: number;
  maxCapacity: number;
  overCapacity: boolean;
  total: number | null;
}

export function includedPersons(room: any): number {
  return Number(room?.base_persons) > 0 ? Number(room.base_persons) : Number(room?.max_adults ?? 0);
}

export function parseChildrenRanges(room: any): ChildRange[] {
  try {
    const raw = room?.children_ranges;
    const list = typeof raw === "string" ? JSON.parse(raw) : (raw ?? []);
    return (list || []).map((x: any) => ({ min: Number(x.min), max: Number(x.max), price: Number(x.price) }));
  } catch {
    return [];
  }
}

export function childPriceForAge(age: number, ranges: ChildRange[]): number {
  const range = ranges.find((r) => age >= r.min && age <= r.max);
  return range ? Number(range.price) : 0;
}

export function quoteBooking(input: QuoteInput): Quote {
  const { room, adults, children, nights } = input;
  const base = Number(room?.price_base ?? 0);
  const single = Number(room?.price_single ?? 0);
  const maxCapacity = Number(room?.max_capacity ?? 0);

  const roomPerNight = adults === 1 && children === 0 && single > 0 ? single : base;
  const included = includedPersons(room);
  const extraCount = included > 0 ? Math.max(0, adults - included) : 0;
  const extraPerNight = Number(room?.price_extra_adult ?? 0);

  const ranges = parseChildrenRanges(room);
  const kids = input.childAges
    .filter((age) => !isNaN(age))
    .map((age, i) => ({ index: i + 1, age, perNight: childPriceForAge(age, ranges) }));
  const childrenPerNight = kids.reduce((sum, k) => sum + k.perNight, 0);

  const breakfastCount = input.breakfast ? adults + children : 0;
  const overCapacity = maxCapacity > 0 && adults + children > maxCapacity;

  const roomTotal = roomPerNight * nights;
  const extraTotal = extraCount * extraPerNight * nights;
  const childrenTotal = childrenPerNight * nights;
  const breakfastTotal = breakfastCount * BREAKFAST_PRICE * nights;

  return {
    nights,
    roomPerNight,
    roomTotal,
    includedPersons: included,
    extraAdults: { count: extraCount, perNight: extraPerNight, total: extraTotal },
    children: kids,
    childrenPerNight,
    childrenTotal,
    breakfastCount,
    breakfastTotal,
    maxCapacity,
    overCapacity,
    total: overCapacity ? null : roomTotal + extraTotal + childrenTotal + breakfastTotal,
  };
}
